(() => {
  const SECTION_SELECTOR = '[data-cardology-judgeme-reviews]';
  const ITEM_SELECTORS = [
    '.jdgm-rev',
    '.jdgm-carousel-item',
    '[data-review-id]',
    '[data-review-uuid]',
    '[class*="review-card"]',
    '[class*="review-item"]'
  ].join(',');

  const textFrom = (root, selectors) => {
    for (const selector of selectors) {
      const node = root.querySelector(selector);
      const value = node && node.textContent ? node.textContent.trim() : '';
      if (value) return value;
    }
    return '';
  };

  const scoreFrom = (root) => {
    const node = root.querySelector('[data-score], .jdgm-rev__rating, [aria-label*="star" i]');
    if (!node) return 5;
    const raw = node.getAttribute('data-score') || node.getAttribute('aria-label') || '';
    const match = String(raw).match(/([1-5](?:\.\d)?)/);
    return match ? Math.max(1, Math.min(5, Math.round(Number(match[1])))) : 5;
  };

  const reviewFrom = (node) => {
    const body = textFrom(node, [
      '.jdgm-rev__body',
      '.jdgm-carousel-item__review-body',
      '.jdgm-carousel-item__review-content',
      '[class*="review-body"]',
      '[class*="review-content"]',
      '[class*="review-text"]'
    ]);
    const author = textFrom(node, [
      '.jdgm-rev__author',
      '.jdgm-carousel-item__reviewer-name',
      '[class*="reviewer-name"]',
      '[class*="review-author"]',
      '[class*="author-name"]'
    ]);
    if (!body || !author) return null;

    const verified = Boolean(node.querySelector(
      '.jdgm-rev__buyer-badge, .jdgm-rev__buyer-badge-wrapper, [class*="verified"], [class*="buyer-badge"]'
    ));

    return {
      body: body.replace(/^\s*[“\"]|[”\"]\s*$/g, ''),
      author,
      score: scoreFrom(node),
      verified
    };
  };

  const signature = (review) => `${review.author}|${review.body}`.toLowerCase();

  class CardologyJudgeMeReviews {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-judgeme-review-track]');
      this.source = section.querySelector('[data-judgeme-review-source]');
      this.status = section.querySelector('[data-judgeme-review-status]');
      this.seen = new Set();
      this.loadingMore = false;
      this.started = false;
      this.observer = null;
      this.sourceObserver = null;
      this.scrollHandler = this.onScroll.bind(this);
      this.init();
    }

    init() {
      if (!this.track || !this.source) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.start();
        }, { rootMargin: '700px 0px' });
        this.observer.observe(this.section);
      } else {
        this.start();
      }
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.collect();

      this.sourceObserver = new MutationObserver(() => {
        this.loadingMore = false;
        this.collect();
      });
      this.sourceObserver.observe(this.source, { childList: true, subtree: true, characterData: true });
      this.track.addEventListener('scroll', this.scrollHandler, { passive: true });

      [350, 1200, 2800, 5000].forEach((delay) => {
        window.setTimeout(() => this.collect(), delay);
      });
    }

    collect() {
      const nodes = Array.from(this.source.querySelectorAll(ITEM_SELECTORS));
      let added = 0;

      nodes.forEach((node) => {
        const review = reviewFrom(node);
        if (!review) return;
        const key = signature(review);
        if (this.seen.has(key)) return;
        this.seen.add(key);
        this.track.appendChild(this.card(review));
        added += 1;
      });

      if (this.seen.size > 0) {
        this.section.classList.add('is-judgeme-ready');
        this.track.setAttribute('aria-busy', 'false');
        if (this.status) {
          this.status.remove();
          this.status = null;
        }
      } else if (this.status && this.started) {
        this.status.textContent = 'Loading customer reviews…';
      }

      if (added && this.track.scrollWidth <= this.track.clientWidth * 1.6) this.requestMore();
    }

    card(review) {
      const article = document.createElement('article');
      article.className = 'ch-customer-review-card';

      const stars = document.createElement('div');
      stars.className = 'ch-customer-review-card__stars';
      stars.setAttribute('aria-label', `${review.score} out of 5 stars`);
      stars.textContent = '★'.repeat(review.score) + '☆'.repeat(5 - review.score);

      const body = document.createElement('p');
      body.className = 'ch-customer-review-card__text';
      body.textContent = `“${review.body}”`;

      const authorWrap = document.createElement('div');
      authorWrap.className = 'ch-customer-review-card__author';

      const author = document.createElement('p');
      author.className = 'ch-customer-review-card__name';
      author.textContent = review.author;

      const verified = document.createElement('p');
      verified.className = 'ch-customer-review-card__verified';
      verified.textContent = review.verified ? 'Verified Buyer' : 'Judge.me Review';

      authorWrap.append(author, verified);
      article.append(stars, body, authorWrap);
      return article;
    }

    onScroll() {
      const remaining = this.track.scrollWidth - this.track.scrollLeft - this.track.clientWidth;
      if (remaining < this.track.clientWidth * 1.25) this.requestMore();
    }

    requestMore() {
      if (this.loadingMore) return;

      const candidates = Array.from(this.source.querySelectorAll('button, a'));
      const next = candidates.find((node) => {
        if (node.disabled || node.getAttribute('aria-disabled') === 'true') return false;
        const label = [
          node.getAttribute('aria-label'),
          node.getAttribute('title'),
          node.textContent,
          node.className
        ].filter(Boolean).join(' ').toLowerCase();
        return /load more|show more|next|paginate__next|arrow-right/.test(label);
      });

      if (!next) return;
      this.loadingMore = true;
      next.click();
      window.setTimeout(() => {
        this.loadingMore = false;
        this.collect();
      }, 1200);
    }
  }

  const boot = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.dataset.judgemeBound === 'true') return;
      section.dataset.judgemeBound = 'true';
      new CardologyJudgeMeReviews(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
